import * as Yup from 'yup';
import { DateFormats, EMPTY_STRING, NAME_MATCHES } from '@uplift/constants';
import { AddressType, YesNo } from '@uplift/types';
import { useL10n } from '@uplift/hooks';
import { VALIDATION_ADDRESS, VALIDATION_ADDRESS_REQUIRED, VALIDATION_CITY, VALIDATION_INSURANCE_GROUP_NUMBER, VALIDATION_INSURANCE_SUBSCRIBER_ID, VALIDATION_STATE, VALIDATION_ZIP } from '@uplift/validations';
import { CreateInsuranceInput, InsuranceCompany } from '@uplift/api';
import { useWithAddressVerify } from '../../../AddressVerification';
import { createDobFieldValidation } from '../../../Inputs';
import { useEditableSectionsContext } from '../../../EditableSection';
import { CalendarDateService } from '@uplift/services';
import { AddInsuranceSection, InsuranceSection } from '../Settings/types';
import { useInsuranceCompaniesQuery, useUserProfileQuery, useUserQuery } from '@uplift/queries';
import { AddressFormType } from '../forms/use-address-form.hook';
import { useUpdateSettings } from './use-update-settings.hook';
import { getDateFormattedForDB } from '../Settings/helpers';
import { useInsurances, useSaveInsurance } from './use-insurance.hook';

export type InsuranceFormType = {
  insuranceProvider?: string;
  memberId: string;
  groupNumber: string;
  insuranceCardFront: string | null;
  insuranceCardBack: string | null;
  isPrimarySubscriber: YesNo;
  primarySubscriberFirstName: string;
  primarySubscriberLastName: string;
  primarySubscriberDateOfBirth: string | null;
  isPrimaryInsurance: YesNo | null;
  patientId: string;
} & AddressFormType;

const emptyInitialValues: InsuranceFormType = {
  insuranceProvider: EMPTY_STRING,
  memberId: EMPTY_STRING,
  groupNumber: EMPTY_STRING,
  insuranceCardFront: null,
  insuranceCardBack: null,
  isPrimarySubscriber: YesNo.Yes,
  primarySubscriberFirstName: EMPTY_STRING,
  primarySubscriberLastName: EMPTY_STRING,
  primarySubscriberDateOfBirth: EMPTY_STRING,
  address1: EMPTY_STRING,
  address2: EMPTY_STRING,
  city: EMPTY_STRING,
  state: EMPTY_STRING,
  zip: EMPTY_STRING,
  patientId: EMPTY_STRING,
  isPrimaryInsurance: null,
};

export const useInsuranceForm = (
  userId: string,
  insuranceSection: InsuranceSection,
  isAddInsurance = false,
  onClose?: () => void
) => {
  const l10n = useL10n();
  const { data: user } = useUserQuery(userId, { suspense: true });
  const { data: userProfile } = useUserProfileQuery(userId, { suspense: true });
  const { onFormSubmitSuccess, onFormSubmitError } = useUpdateSettings('insurance', userId);
  const { saveInsurance } = useSaveInsurance(userId);
  const withAddressVerify = useWithAddressVerify();
  const { validationSchema, ...validations } = useInsuranceFormValidation(userId);
  const { editKey } = useEditableSectionsContext();
  const { data: insurancesData } = useInsurances(userId);
  const insuranceData = insurancesData?.[insuranceSection];

  const { data: insuranceCompanies } = useInsuranceCompaniesQuery();

  const insuranceFormik = () => {
    const isPrimarySubscriber = (insuranceData?.isPrimary === undefined || insuranceData?.isPrimary) ? YesNo.Yes : YesNo.No;
    const isPrimary = isPrimarySubscriber === YesNo.Yes;
    const checkIsPrimary = (value?: string | null) => (isPrimary || !value) ? EMPTY_STRING : value;

    const initialValues: InsuranceFormType = isAddInsurance
      ? emptyInitialValues
      : {
        insuranceProvider: insuranceData?.companyId ?? EMPTY_STRING,
        memberId: insuranceData?.subscriberId ?? EMPTY_STRING,
        groupNumber: insuranceData?.groupNumber ?? EMPTY_STRING,
        insuranceCardFront: insuranceData?.insuranceCardFront ?? null,
        insuranceCardBack: insuranceData?.insuranceCardBack ?? null,
        isPrimarySubscriber: isPrimarySubscriber,
        primarySubscriberFirstName: checkIsPrimary(insuranceData?.firstName),
        primarySubscriberLastName: checkIsPrimary(insuranceData?.lastName),
        primarySubscriberDateOfBirth: checkIsPrimary(insuranceData?.birthDate) ? CalendarDateService.date(insuranceData?.birthDate).format(DateFormats.DEFAULT_DATE) : EMPTY_STRING,
        address1: checkIsPrimary(insuranceData?.address1),
        address2: checkIsPrimary(insuranceData?.address2),
        city: checkIsPrimary(insuranceData?.city),
        state: checkIsPrimary(insuranceData?.state),
        zip: checkIsPrimary(insuranceData?.zip),
        patientId: userId,
        isPrimaryInsurance: null
      };

    const getInsuranceAddress = (obj: AddressType): CreateInsuranceInput['address'] => {
      // const { address1, address2, city, state, zip } = obj;

      return {
        line1: obj.address1,
        line2: obj.address2 ?? '', // address2 could possibly be null if isPrimary (using profileData address)
        city: obj.city,
        stateId: obj.state,
        zip: obj.zip,
      };
    };

    const onSubmit = withAddressVerify(async (values: InsuranceFormType) => {
      const isPrimary = values.isPrimarySubscriber === YesNo.Yes;
      const address = isPrimary ? getInsuranceAddress({
        address1: userProfile?.address.line1 ?? '',
        address2: userProfile?.address.line2 ?? '',
        city: userProfile?.address.city ?? '',
        state: userProfile?.address.stateId ?? '',
        zip: userProfile?.address.zip ?? '',

      }) : getInsuranceAddress(values);
      const primarySubscriberDetails = isPrimary
        ? {
          firstName: user?.firstName ?? '',
          lastName: user?.lastName ?? '',
          dob: getDateFormattedForDB(userProfile?.dob ?? '')
        }
        : {
          firstName: values.primarySubscriberFirstName,
          lastName: values.primarySubscriberLastName,
          dob: getDateFormattedForDB(values.primarySubscriberDateOfBirth ?? '')
        };

      const update: CreateInsuranceInput = {
        isPrimary: isPrimary,
        companyId: values.insuranceProvider ?? '',
        groupNumber: values.groupNumber,
        subscriberId: values.memberId,
        cardFrontAssetId: values.insuranceCardFront ?? '',
        cardBackAssetId: values.insuranceCardBack ?? '',
        ...primarySubscriberDetails,
        address,
        isSecondaryInsurance: insuranceSection === InsuranceSection.secondaryInsurance,
        patientId: userId,
      };

      // checking this again in case user made a selection, then went back and changed insurance to a non-commercial
      const shouldCheckIfIsPrimary = shouldCheckIfIsPrimaryInsurance({
        primaryInsuranceCompanyId: insurancesData?.[InsuranceSection.primaryInsurance]?.companyId,
        currentInsuranceCompanyId: values.insuranceProvider,
        insuranceCompanies: insuranceCompanies ?? [],
        addInsuranceSection: editKey
      });

      if (shouldCheckIfIsPrimary && values.isPrimaryInsurance === YesNo.Yes) {
        update.shouldSwapInsurances = true;
      }

      try {
        const insurance = await saveInsurance(update);

        if (!insurance) throw new Error();

        const message = isAddInsurance
          ? l10n.accountSettings.insurance.insuranceAdded
          : l10n.accountSettings.insurance.changesSaved;

        onFormSubmitSuccess(message);

        if (onClose) {
          onClose();
        }

      } catch (error) {
        const message = isAddInsurance
          ? l10n.accountSettings.insurance.insuranceCouldNotBeAdded
          : l10n.accountSettings.insurance.insuranceCouldNotBeUpdated;

        onFormSubmitError(message);
      }
    }, {
      shouldCheck: (_address, formValues) => formValues.isPrimarySubscriber === YesNo.No,
      initialAddress: {
        address1: initialValues.address1,
        address2: initialValues.address2,
        city: initialValues.city,
        state: initialValues.state,
        zip: initialValues.zip
      }
    });

    return { initialValues, validationSchema, onSubmit };
  };


  return { insuranceFormik, ...validations };
};


export const useInsuranceFormValidation = (userId: string) => {
  const { data: insuranceCompanies } = useInsuranceCompaniesQuery();
  const { data: insurancesData } = useInsurances(userId);
  const l10n = useL10n();
  const { editKey } = useEditableSectionsContext();

  const insuranceDetailsValidation = Yup.object({
    insuranceProvider: Yup.string().nullable().required(l10n.validations.selectInsurance),
    memberId: VALIDATION_INSURANCE_SUBSCRIBER_ID,
    groupNumber: VALIDATION_INSURANCE_GROUP_NUMBER,
  });

  const insuranceCardsValidation = Yup.object({
    insuranceCardFront: Yup.string().required(l10n.validations.insuranceCardFront),
    insuranceCardBack: Yup.string().required(l10n.validations.insuranceCardBack),
  });

  const primarySubscriberValidation = Yup.object({
    isPrimarySubscriber: Yup.mixed<YesNo>().oneOf([ YesNo.Yes, YesNo.No ]),
    primarySubscriberFirstName: Yup.string().nullable().notRequired()
      .when('isPrimarySubscriber', {
        is: YesNo.No,
        then: s => s
          .matches(NAME_MATCHES, l10n.validations.nameMatches)
          .max(64, l10n.validations.lengthMatches(1, 64))
          .required(l10n.validations.primarySubFirstName),
      }),
    primarySubscriberLastName: Yup.string().nullable().notRequired()
      .when('isPrimarySubscriber', {
        is: YesNo.No,
        then: s => s
          .matches(NAME_MATCHES, l10n.validations.nameMatches)
          .max(64, l10n.validations.lengthMatches(1, 64))
          .required(l10n.validations.primarySubLastName)
      }),
    primarySubscriberDateOfBirth: Yup.mixed().nullable().notRequired()
      .when('isPrimarySubscriber', {
        is: YesNo.No,
        then: () => createDobFieldValidation(l10n),
      }),
    address1: Yup.mixed().nullable().notRequired()
      .when('isPrimarySubscriber', {
        is: YesNo.No,
        then: _s => VALIDATION_ADDRESS_REQUIRED.required(l10n.validations.primarySubStreetAddress)
      }),
    address2: VALIDATION_ADDRESS.nullable().notRequired(),
    city: Yup.mixed().nullable().notRequired()
      .when('isPrimarySubscriber', {
        is: YesNo.No,
        then: _s => VALIDATION_CITY.required(l10n.validations.primarySubCity)
      }),
    state: Yup.mixed().nullable().notRequired()
      .when('isPrimarySubscriber', {
        is: YesNo.No,
        then: _s => VALIDATION_STATE.required(l10n.validations.primarySubState)
      }),
    zip: Yup.mixed().nullable().notRequired()
      .when('isPrimarySubscriber', {
        is: YesNo.No,
        then: _s => VALIDATION_ZIP.required(l10n.validations.primarySubZip)
      }),
  });

  const insuranceDetailsIsPrimaryInsuranceValidation = Yup.object({
    isPrimaryInsurance: Yup.mixed<YesNo>().oneOf([ YesNo.Yes, YesNo.No ]).nullable().when('insuranceProvider', {
      is: (value: string) => {

        return shouldCheckIfIsPrimaryInsurance({
          primaryInsuranceCompanyId: insurancesData?.[InsuranceSection.primaryInsurance]?.companyId,
          currentInsuranceCompanyId: value,
          insuranceCompanies: insuranceCompanies ?? [],
          addInsuranceSection: editKey
        });
      },
      then: s => s.required(),
    })
  });

  const validationSchema: Yup.ObjectSchema<any> = insuranceDetailsValidation
    .concat(insuranceCardsValidation)
    .concat(primarySubscriberValidation)
    .concat(insuranceDetailsIsPrimaryInsuranceValidation);

  const isInsuranceDetailsValid = (values: InsuranceFormType) => {
    return insuranceDetailsValidation.isValidSync(values);
  };
  const isInsuranceDetailsIsPrimaryInsuranceValid = (values: InsuranceFormType) => {
    return insuranceDetailsIsPrimaryInsuranceValidation.isValidSync(values);
  };
  const isInsuranceCardsValid = (values: InsuranceFormType) => {
    return insuranceCardsValidation.isValidSync(values);
  };
  const isPrimarySubscriberValid = (values: InsuranceFormType) => {
    return primarySubscriberValidation.isValidSync(values);
  };

  return {
    validationSchema,
    isInsuranceDetailsValid,
    isInsuranceCardsValid,
    isPrimarySubscriberValid,
    isInsuranceDetailsIsPrimaryInsuranceValid
  };
};

type ShouldCheckIfIsPrimaryInsuranceArgs = {
  primaryInsuranceCompanyId: string | undefined;
  currentInsuranceCompanyId: string | undefined;
  insuranceCompanies: InsuranceCompany[];
  addInsuranceSection: AddInsuranceSection | string | null;
};

export const checkInsuranceCompanyIsCommerical = (company?: InsuranceCompany) => {
  if (!company) return false;
  return ![ 'medicaid', 'medicare' ].includes(company.metaGrouping);
};

export const shouldCheckIfIsPrimaryInsurance = (args: ShouldCheckIfIsPrimaryInsuranceArgs) => {
  let shouldCheck = false;

  if (args.addInsuranceSection === AddInsuranceSection.addSecondaryInsurance && args.primaryInsuranceCompanyId) {
    const primaryInsuranceCompany = args.insuranceCompanies.find((c) => c.id === args.primaryInsuranceCompanyId);
    const primaryIsCommerical = checkInsuranceCompanyIsCommerical(primaryInsuranceCompany);
    const currentInsuranceCompany = args.insuranceCompanies.find((c) => c.id === args.currentInsuranceCompanyId);
    const currentIsCommercial = checkInsuranceCompanyIsCommerical(currentInsuranceCompany);

    shouldCheck = primaryIsCommerical && currentIsCommercial;
  }

  return shouldCheck;
};
